import { logErrorMessage, logInfoMessage } from '@koda/koda-logger/loggers';
import { z } from 'zod';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { isClientDebugEnabled } from '../../../../src/lib/clientDebugEnabled';
import { EnrollmentError } from '../../../../src/lib/errors';
import { LOG_CLIENT_NAME, LOG_MEMBER_PROCESS_NAME } from '../../../../src/lib/instrumentation/consts';
import { TRANSACTION_AMOUNT } from './consts';
import { Flow, OVER_18_DENMARK_FLOW, resolveFlow } from './flow';
import { MemberSchema } from './schemas/member';
import { getSubmitMemberSchema } from './schemas/profiles';

export type Member = z.input<typeof MemberSchema>;

const defaultMemberData: Member = {
  BankRegNumber: '',
  BankAccountNumber: '',
  AbaRoutingNumber: '',
  SwiftBicCode: '',
  IbanCode: '',
  BankAddress: 'DK',
  BankName: '',
  Cpr: '',
  CVRNo: '',
  CompanyName: '',
  PublisherName: '',
  Name: '',
  Address1: '',
  Address2: '',
  Zip: '',
  City: '',
  CountryCode: 'DK',
  HasDanishCpr: null,
  Email: '',
  MobilePhone: '',
  Phone: '',
  LanguageCode: 'da',
  Consent: null,
  ArtistName: '',
  BandName: '',
  UserType: 'Member',
  Amount: TRANSACTION_AMOUNT / 100,
  TransactionId: '',
  OrderId: '',
  TIN: '',
  Fodselsdato: '',
  Vaergenavn: '',
  Vaergeemail: '',
  Vaergetelefon: '',
  VaergeCpr: '',
  NewsletterDa: false,
  NewsletterFo: false,
  NewsletterGl: false,
  CriiptoValidationToken: '',
  lockedFields: {
    Name: false,
    Country: false,
    Fodselsdato: false,
    HasDanishCpr: false,
  },
  didAcceptTerms: false,
};

type MemberStore = {
  loadingIfValid: boolean;
  setLoadingIfValid: (loading: boolean) => void;
  changedFromForeignToDenmarkFlow: boolean;
  setChangedFromForeignToDenmarkFlow: (show: boolean) => void;
  changedToDenmarkFromForeignFlow: boolean;
  setChangedToDenmarkFromForeignFlow: (show: boolean) => void;
  dismissedMitIdToken: string | null;
  setDismissedMitIdToken: (token: string | null) => void;
  mitIdAuthInProgress: boolean;
  setMitIdAuthInProgress: (value: boolean) => void;
  triggerMitIdLogin: (() => Promise<unknown> | void) | null;
  setTriggerMitIdLogin: (callback: (() => Promise<unknown> | void) | null) => void;
  loadingTinIfExists: boolean;
  setLoadingTinIfExists: (loading: boolean) => void;
  currentFlow: Flow;
  setCurrentFlow: (flow: Flow) => void;
  isHydrated: boolean;
  showErrors: boolean;
  setIsHydrated: () => void;
  setShowErrors: (show: boolean) => void;
  member: Member;
  cprValid: boolean | null;
  setCprValid: (value: boolean | null) => void;
  tinValid: boolean | null;
  setTinValid: (value: boolean | null) => void;
  tinInvalidFormats: string[] | null;
  setTinInvalidFormats: (formats: string[] | null) => void;
  setMemberData: (data: Partial<Member>) => void;
  lockMitIdFields: (
    fields: Partial<{ Name: boolean; Country: boolean; Fodselsdato: boolean; HasDanishCpr: boolean }>,
  ) => void;
  setBirthday: (date: Date | undefined) => void;
  setIsEditing: (isEditing: boolean) => void;
  toggleIsEditing: () => void;
  resetMemberData: () => void;
  isEditing: boolean;
  addressReceivedFromMitId: boolean;
  setAddressReceivedFromMitId: (value: boolean) => void;
  mitIdError: string | null;
  setMitIdError: (error: string | null) => void;
  resetMitIdData: () => void;
  fullReset: () => void;
};

export const useMemberStore = create<MemberStore>()(
  persist(
    set => ({
      isHydrated: false,
      showErrors: false,
      currentFlow: OVER_18_DENMARK_FLOW,
      cprValid: null,
      tinValid: null,
      tinInvalidFormats: null,
      mitIdError: null,
      addressReceivedFromMitId: false,
      dismissedMitIdToken: null,
      mitIdAuthInProgress: false,
      triggerMitIdLogin: null,
      loadingIfValid: false,
      changedFromForeignToDenmarkFlow: false,
      changedToDenmarkFromForeignFlow: false,
      loadingTinIfExists: false,
      setLoadingIfValid: (loading: boolean) => set(() => ({ loadingIfValid: loading })),
      setChangedFromForeignToDenmarkFlow: (show: boolean) => set(() => ({ changedFromForeignToDenmarkFlow: show })),
      setChangedToDenmarkFromForeignFlow: (show: boolean) => set(() => ({ changedToDenmarkFromForeignFlow: show })),
      setDismissedMitIdToken: (token: string | null) => set(() => ({ dismissedMitIdToken: token })),
      setMitIdAuthInProgress: (value: boolean) => set(() => ({ mitIdAuthInProgress: value })),
      setTriggerMitIdLogin: (callback: (() => Promise<unknown> | void) | null) =>
        set(() => ({ triggerMitIdLogin: callback })),
      setLoadingTinIfExists: (loading: boolean) => set(() => ({ loadingTinIfExists: loading })),
      setCurrentFlow: (flow: Flow) => set(() => ({ currentFlow: flow })),
      setAddressReceivedFromMitId: (value: boolean) => set(() => ({ addressReceivedFromMitId: value })),
      setMitIdError: (error: string | null) => set(state => ({ ...state, mitIdError: error })),
      setCprValid: (value: boolean | null) => set(state => ({ ...state, cprValid: value })),
      setTinValid: (value: boolean | null) => set(state => ({ ...state, tinValid: value })),
      setTinInvalidFormats: (formats: string[] | null) => set(state => ({ ...state, tinInvalidFormats: formats })),
      setShowErrors: (show: boolean) => set(state => ({ ...state, showErrors: show })),
      member: defaultMemberData,
      isEditing: false,
      setIsEditing: (isEditing: boolean) => set(() => ({ isEditing })),
      toggleIsEditing: () => set(state => ({ isEditing: !state.isEditing })),
      setIsHydrated: () => set(state => ({ ...state, isHydrated: true })),
      setMemberData: (data: Exclude<Partial<Member>, 'TransactionId'>) =>
        set(state => {
          const nextCountryCode = data.CountryCode ? data.CountryCode : state.member.CountryCode;
          return {
            member: { ...state.member, ...data },
            currentFlow: resolveFlow(nextCountryCode),
          };
        }),
      resetMemberData: () =>
        set(() => ({
          member: defaultMemberData,
          currentFlow: OVER_18_DENMARK_FLOW,
          changedFromForeignToDenmarkFlow: false,
          changedToDenmarkFromForeignFlow: false,
          tinValid: null,
          tinInvalidFormats: null,
          loadingTinIfExists: false,
        })),
      setBirthday: (date: Date | undefined) =>
        set(state => {
          if (!date || isNaN(date.getTime())) {
            return { member: { ...state.member, Fodselsdato: '' } };
          }
          // Normalize to UTC midnight to avoid timezone day-shift when re-parsed
          const utcMidnight = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
          return { member: { ...state.member, Fodselsdato: utcMidnight.toISOString() } };
        }),
      lockMitIdFields: (
        fields: Partial<{ Name: boolean; Country: boolean; Fodselsdato: boolean; HasDanishCpr: boolean }>,
      ) =>
        set(state => ({
          member: { ...state.member, lockedFields: { ...state.member.lockedFields, ...fields } },
        })),
      resetMitIdData: () =>
        set(state => ({
          cprValid: null,
          tinValid: null,
          tinInvalidFormats: null,
          loadingIfValid: false,
          mitIdAuthInProgress: false,
          dismissedMitIdToken: null,
          changedFromForeignToDenmarkFlow: false,
          changedToDenmarkFromForeignFlow: false,
          addressReceivedFromMitId: false,
          member: {
            ...state.member,
            CriiptoValidationToken: '',
            Cpr: '',
            lockedFields: { Name: false, Country: false, Fodselsdato: false, HasDanishCpr: false },
          },
        })),
      fullReset: () =>
        set(() => ({
          member: defaultMemberData,
          currentFlow: OVER_18_DENMARK_FLOW,
          cprValid: null,
          tinValid: null,
          tinInvalidFormats: null,
          mitIdError: null,
          dismissedMitIdToken: null,
          mitIdAuthInProgress: false,
          triggerMitIdLogin: null,
          loadingIfValid: false,
          changedFromForeignToDenmarkFlow: false,
          changedToDenmarkFromForeignFlow: false,
          loadingTinIfExists: false,
          showErrors: false,
          isEditing: false,
          addressReceivedFromMitId: false,
        })),
    }),
    {
      name: 'member-store',
      partialize: state => ({
        member: state.member,
        currentFlow: state.currentFlow,
        cprValid: state.cprValid,
        tinValid: state.tinValid,
        addressReceivedFromMitId: state.addressReceivedFromMitId,
      }),
      storage: createJSONStorage(() => sessionStorage), // store in session storage to avoid persisting sensitive data
      onRehydrateStorage: () => async (state, error) => {
        if (isClientDebugEnabled()) {
          await logInfoMessage({
            instance: LOG_CLIENT_NAME,
            processName: LOG_MEMBER_PROCESS_NAME,
            action: 'hydrating',
            step: 'start',
          });
        }

        if (error) {
          const transactionId = state?.member?.TransactionId || 'N/A';
          logErrorMessage(error instanceof Error ? error : new Error('Unknown error during hydration'), {
            instance: LOG_CLIENT_NAME,
            processName: LOG_MEMBER_PROCESS_NAME,
            action: 'hydration',
            step: 'error',
            transactionId,
          });
        } else {
          const bankAddress = state?.member?.BankAddress?.trim();
          const countryCode = state?.member?.CountryCode;

          if (!bankAddress && countryCode) {
            state?.setMemberData({ BankAddress: countryCode });
          }

          state?.setMitIdAuthInProgress(false);
          state?.setTriggerMitIdLogin(null);
          state?.setDismissedMitIdToken(null);
          state?.setChangedFromForeignToDenmarkFlow(false);
          state?.setChangedToDenmarkFromForeignFlow(false);
          state?.setIsHydrated();
        }
        if (isClientDebugEnabled()) {
          await logInfoMessage({
            instance: LOG_CLIENT_NAME,
            processName: LOG_MEMBER_PROCESS_NAME,
            action: 'hydrating',
            step: 'end',
          });
        }
      },
    },
  ),
);

export const validateMember = (data: Partial<Member>) => {
  try {
    const submitMemberData =
      data.CountryCode === 'GL'
        ? {
            // For Greenland, CPR is equal to TIN, and we need both values, so we overwrite TIN with CPR value before validation
            ...data,
            TIN: data.Cpr,
          }
        : data;

    const submitSchema = getSubmitMemberSchema({
      CountryCode: String(submitMemberData.CountryCode || ''),
      HasDanishCpr: submitMemberData.HasDanishCpr,
    });

    return submitSchema.parse(submitMemberData);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new EnrollmentError(JSON.stringify(error.flatten().fieldErrors));
    }
    throw new EnrollmentError('Unknown validation error occurred');
  }
};
